import { z } from 'zod'

// Runtime-editable settings, stored one row per key in intra.settings.
//
// intra.settings holds text values; the key says how to read them. This is
// the "zod schema per key in packages/shared" that V140__settings.sql
// promises, so the admin UI, the PUT handler and the reader all agree on
// what a value may be.

export const SETTINGS = {
  // The club's own timezone: the wall-clock a booking at 14:00 means, and
  // the second hand of the header clock next to UTC.
  clubTimezone: 'club.timezone',
  // Links on the public membership application form (/join): "not looking
  // to join? contact us instead", and the privacy statement the applicant
  // accepts. An empty value leaves the link out.
  membershipContactUrl: 'membership.contactUrl',
  membershipPrivacyUrl: 'membership.privacyUrl',
  // Where a public contact-form enquiry is emailed, by its nature. Empty
  // means not configured: the form answers 503 rather than losing it.
  contactEmailTraining: 'contact.emailTraining',
  contactEmailMembership: 'contact.emailMembership',
  contactEmailOther: 'contact.emailOther',
  // Automatic email timing (apps/api/src/email/jobs). Offsets are days
  // before the expiry date, comma-separated; the hour is the club clock.
  mailBookingReminderHour: 'mail.bookingReminderHour',
  mailLicenceReminderDays: 'mail.licenceReminderDays',
  mailInstructorExpiryNoticeDays: 'mail.instructorExpiryNoticeDays',
  mailAircraftDocReminderDays: 'mail.aircraftDocReminderDays',
  mailInventoryExpiryReminderDays: 'mail.inventoryExpiryReminderDays',
  // What the flight log's "close your flight plan" reminder dials. Empty
  // means not configured.
  flightPlanPhone: 'flightLog.flightPlanPhone',
  // The airfield a new flight log entry departs from and returns to.
  homeBase: 'flightLog.homeBase',
  // Above these the entry form warns that a flight's airborne time (hours)
  // or its taxi-out, off-block to takeoff (minutes), looks excessive.
  flightDurationWarningHours: 'flightLog.flightDurationWarningHours',
  taxiWarningMinutes: 'flightLog.taxiWarningMinutes',
  // The travel rates an expense claim pays, in euros: own car per km, and
  // per day the full and partial daily allowances and the meal allowance.
  // Seeded at the Tax Administration's 2026 tax-free maximums
  // (db/flyway/static/sql/V440__expense_settings.sql); EXPENSE_RATE_DEFAULTS
  // below is the fallback.
  expenseMileageRate: 'expense.mileageRate',
  expenseDailyAllowanceFull: 'expense.dailyAllowanceFull',
  expenseDailyAllowancePartial: 'expense.dailyAllowancePartial',
  expenseMealAllowance: 'expense.mealAllowance',
} as const

// The defaults, used while a setting is missing or unreadable, and what
// db/flyway/static/sql/V100__email_settings.sql seeds. From #16: the
// member 30 and 10 days ahead, the head of training the day before an
// instructor's document lapses, the fleet a month and a day ahead.
export const MAIL_SETTING_DEFAULTS = {
  bookingReminderHour: 18,
  licenceReminderDays: [30, 10],
  instructorExpiryNoticeDays: [1],
  aircraftDocReminderDays: [30, 1],
  inventoryExpiryReminderDays: [30, 7],
} as const

export type SettingKey = (typeof SETTINGS)[keyof typeof SETTINGS]

// The fallback when the row is missing or unreadable, and what
// db/flyway/static/sql/V30__settings.sql seeds. JuPi flies from Helsinki;
// the setting exists because the club has said it may not always.
export const DEFAULT_CLUB_TIMEZONE = 'Europe/Helsinki'

// An IANA zone name, checked by asking Intl rather than against a list --
// the list is the runtime's, and it grows.
export function isValidTimezone(name: string): boolean {
  try {
    new Intl.DateTimeFormat('en-GB', { timeZone: name })
    return true
  } catch {
    return false
  }
}

export const ClubTimezoneSchema = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .refine(isValidTimezone, 'Not an IANA timezone name')

// Keys with a schema are validated on write; anything else is free text,
// which is what makes adding a setting an INSERT rather than a deploy.
// An absolute https link, or empty for none.
export const OptionalUrlSchema = z
  .string()
  .trim()
  .max(500)
  .refine((v) => v === '' || /^https:\/\/[^\s]+$/.test(v), 'Must be an https:// link, or empty')

// One address, or empty for none.
export const OptionalEmailSchema = z
  .string()
  .trim()
  .max(254)
  .refine((v) => v === '' || z.string().email().safeParse(v).success, 'Must be an email address, or empty')

// A phone number as people write one ("+358 20 123 4567"), or empty.
export const OptionalPhoneSchema = z
  .string()
  .trim()
  .max(40)
  .refine((v) => v === '' || /^\+?[\d\s()-]{5,}$/.test(v), 'Must be a phone number, or empty')

// An airfield code such as EFHV, as the flight log writes it.
export const AirfieldSettingSchema = z
  .string()
  .trim()
  .regex(/^[A-Z0-9]{2,10}$/, 'An airfield code such as EFHV, in capitals')

// A whole number of hours or minutes for a warning level; 0 turns it off.
export const WarningHoursSchema = z.string().trim().regex(/^\d{1,2}$/, 'Whole hours, 0-99')
export const WarningMinutesSchema = z.string().trim().regex(/^\d{1,3}$/, 'Whole minutes, 0-999')

export const HourSchema = z.string().trim().regex(/^([01]?\d|2[0-3])$/, 'An hour, 0-23')
export const DayOffsetsSchema = z
  .string()
  .trim()
  .regex(/^\d{1,3}(\s*,\s*\d{1,3})*$/, 'Days before, comma-separated, e.g. 30,10')
  .refine((v) => v.split(',').every((d) => Number(d) <= 365), 'At most 365 days')

// A rate in euros: positive, at most four decimals ("0.55", "54").
export const RateSettingSchema = z
  .string()
  .trim()
  .regex(/^\d{1,4}(\.\d{1,4})?$/, 'A rate in euros, e.g. 0.55')
  .refine((v) => Number(v) > 0, 'Must be more than zero')

export const SETTING_SCHEMAS: Partial<Record<string, z.ZodType<unknown>>> = {
  [SETTINGS.mailBookingReminderHour]: HourSchema,
  [SETTINGS.mailLicenceReminderDays]: DayOffsetsSchema,
  [SETTINGS.mailInstructorExpiryNoticeDays]: DayOffsetsSchema,
  [SETTINGS.mailAircraftDocReminderDays]: DayOffsetsSchema,
  [SETTINGS.mailInventoryExpiryReminderDays]: DayOffsetsSchema,
  [SETTINGS.clubTimezone]: ClubTimezoneSchema,
  [SETTINGS.membershipContactUrl]: OptionalUrlSchema,
  [SETTINGS.membershipPrivacyUrl]: OptionalUrlSchema,
  [SETTINGS.contactEmailTraining]: OptionalEmailSchema,
  [SETTINGS.contactEmailMembership]: OptionalEmailSchema,
  [SETTINGS.contactEmailOther]: OptionalEmailSchema,
  [SETTINGS.flightPlanPhone]: OptionalPhoneSchema,
  [SETTINGS.homeBase]: AirfieldSettingSchema,
  [SETTINGS.flightDurationWarningHours]: WarningHoursSchema,
  [SETTINGS.taxiWarningMinutes]: WarningMinutesSchema,
  [SETTINGS.expenseMileageRate]: RateSettingSchema,
  [SETTINGS.expenseDailyAllowanceFull]: RateSettingSchema,
  [SETTINGS.expenseDailyAllowancePartial]: RateSettingSchema,
  [SETTINGS.expenseMealAllowance]: RateSettingSchema,
}

// GET /api/time. Public, unauthenticated: a clock the client cannot read
// before it has logged in is a clock that lies on the login screen.
//
// `epochMs` is what the client measures skew against; `timezone` is the
// configured club zone, sent with the time so a single poll keeps both the
// clock and the formatters current.
export interface ServerTime {
  utcIso: string
  epochMs: number
  timezone: string
  // Wrangler's APP_ENV var (development/beta/production) -- see
  // apps/api/wrangler.toml. Rides along so the header can flag a non-prod
  // deployment, the way mik-ng's header shows "beta" next to its logo.
  appEnv: string
  // Semver build version (0.1.3, or 0.1.3-beta.21+e10058e on beta), set by
  // _deploy.yml as the Worker's APP_VERSION var. 'dev' when running locally.
  appVersion: string
  // The home airfield (flightLog.homeBase) and where it is, for the sun times
  // in the header and calendar; null when the code is not a known point.
  homeBase: { code: string; lat: number; lon: number } | null
}
